import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { applyShopTheme } from '@/lib/shopApi';
import { Lock, At, Storefront, Warning, Buildings } from '@phosphor-icons/react';

export default function ShopLoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [leverancier, setLeverancier] = useState(null);

  // Debounced lookup: zodra de klant een geldig e-mailadres invult, toon leverancier
  useEffect(() => {
    const em = email.trim().toLowerCase();
    if (!em || !em.includes('@') || em.length < 5) {
      setLeverancier(null);
      applyShopTheme(null);
      return;
    }
    const t = setTimeout(() => {
      axios
        .get(`${process.env.REACT_APP_BACKEND_URL}/api/shop/leverancier`, { params: { email: em } })
        .then((r) => { setLeverancier(r.data); applyShopTheme(r.data?.primary_kleur); })
        .catch(() => { setLeverancier(null); applyShopTheme(null); });
    }, 400);
    return () => clearTimeout(t);
  }, [email]);

  const onLogin = async (e) => {
    e.preventDefault();
    setBusy(true); setErr('');
    try {
      const r = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/shop/login`, { email, password: pw });
      localStorage.setItem('shop_token', r.data.token);
      navigate('/shop/bestellen');
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Login mislukt');
    } finally {
      setBusy(false);
    }
  };

  const apiBase = process.env.REACT_APP_BACKEND_URL;
  const logoUrl = leverancier?.logo ? `${apiBase}${leverancier.logo}` : null;

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: 'linear-gradient(to bottom right, var(--shop-primary), var(--shop-primary-dark))' }}>
      <form onSubmit={onLogin} className="bg-white shadow-2xl max-w-md w-full p-8" data-testid="shop-login-form">
        {/* Bedrijf-hero (verschijnt zodra e-mail wordt herkend) */}
        {leverancier ? (
          <div className="text-center mb-6 pb-5" style={{ borderBottom: '2px solid color-mix(in srgb, var(--shop-primary) 10%, transparent)' }} data-testid="shop-login-leverancier">
            {logoUrl ? (
              <img src={logoUrl} alt={leverancier.naam} className="h-14 mx-auto mb-2 object-contain" />
            ) : (
              <Buildings size={36} weight="bold" className="shop-text-primary mx-auto mb-1" />
            )}
            <div className="text-xs uppercase font-bold text-gray-400 tracking-widest">Online Shop van</div>
            <div className="text-2xl font-black shop-text-primary leading-tight">{leverancier.naam}</div>
            {leverancier.tel && <div className="text-[11px] text-gray-500 mt-1">📞 {leverancier.tel}</div>}
          </div>
        ) : (
          <div className="flex items-center gap-2 mb-6">
            <Storefront size={28} weight="bold" className="shop-text-primary" />
            <div>
              <div className="text-xl font-black shop-text-primary">Online Shop</div>
              <div className="text-xs text-gray-500">Inloggen voor zakelijke klanten</div>
            </div>
          </div>
        )}
        {err && (
          <div className="mb-4 p-2 bg-red-50 border border-red-200 text-xs text-red-700 flex items-start gap-1" data-testid="shop-login-error">
            <Warning size={14} className="mt-0.5 shrink-0" /> {err}
          </div>
        )}
        <label className="block text-xs font-bold text-gray-600 uppercase mb-1">E-mailadres</label>
        <div className="relative mb-4">
          <At size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="email" className="w-full border-2 border-gray-200 shop-ring-primary outline-none pl-9 pr-3 py-2.5 text-sm" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="naam@bedrijf.be" autoFocus required data-testid="shop-login-email" />
        </div>
        <label className="block text-xs font-bold text-gray-600 uppercase mb-1">Wachtwoord</label>
        <div className="relative mb-6">
          <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input type="password" className="w-full border-2 border-gray-200 shop-ring-primary outline-none pl-9 pr-3 py-2.5 text-sm" value={pw} onChange={(e) => setPw(e.target.value)} required data-testid="shop-login-password" />
        </div>
        <button type="submit" disabled={busy} className="w-full shop-bg-primary text-white font-bold py-3 disabled:opacity-50" data-testid="shop-login-submit">
          {busy ? 'Aanmelden…' : 'Aanmelden'}
        </button>
        <p className="text-[11px] text-gray-500 mt-4 text-center">
          Nog geen account? Vraag uw leverancier om een uitnodiging.
        </p>
      </form>
    </div>
  );
}
